import React, { useEffect, useMemo, useState } from "react"
import { Trash2 } from "lucide-react"
import useApi from "../../hooks/useApi.js"

export default function ReviewPagesStep({ t, profile, onReviewed }) {
  const api = useApi()
  const [ pages, setPages ] = useState([])
  const [ loading, setLoading ] = useState(true)
  const [ submitting, setSubmitting ] = useState(false)
  const [ error, setError ] = useState(null)

  const [ newPath, setNewPath ] = useState("")
  const [ newTitle, setNewTitle ] = useState("")
  const [ adding, setAdding ] = useState(false)
  const [ addError, setAddError ] = useState(null)
  const [ removingId, setRemovingId ] = useState(null)
  const [ includingPath, setIncludingPath ] = useState(null)

  useEffect(() => {
    let cancelled = false
    api.get("/api/v1/shop/pages")
      .then((data) => { if (!cancelled) { setPages(data); setLoading(false) } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false) } })
    return () => { cancelled = true }
  }, [ api ])

  const existingPaths = useMemo(() => new Set(pages.map((p) => p.path)), [ pages ])
  const blockedNotAdded = useMemo(() => {
    const all = profile?.blocked_paths || []
    return all.filter((path) => !existingPaths.has(path))
  }, [ profile, existingPaths ])

  const addPage = async (e) => {
    e.preventDefault()
    setAdding(true)
    setAddError(null)
    try {
      const created = await api.post("/api/v1/shop/pages", {
        page: { path: newPath, title: newTitle || undefined }
      })
      setPages((prev) => [ ...prev, created ].sort((a, b) => a.path.localeCompare(b.path)))
      setNewPath("")
      setNewTitle("")
    } catch (err) {
      setAddError(err.message)
    } finally {
      setAdding(false)
    }
  }

  const includeBlocked = async (path) => {
    setIncludingPath(path)
    setError(null)
    try {
      const created = await api.post("/api/v1/shop/pages", { page: { path } })
      setPages((prev) => [ ...prev, created ].sort((a, b) => a.path.localeCompare(b.path)))
    } catch (err) {
      setError(err.message)
    } finally {
      setIncludingPath(null)
    }
  }

  const removePage = async (page) => {
    setRemovingId(page.id)
    try {
      await api.destroy(`/api/v1/shop/pages/${page.id}`)
      setPages((prev) => prev.filter((p) => p.id !== page.id))
    } catch (err) {
      setError(err.message)
    } finally {
      setRemovingId(null)
    }
  }

  const markReviewed = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await api.patch("/api/v1/shop/setting", {
        setting: { pages_reviewed_at: new Date().toISOString() }
      })
      onReviewed()
    } catch (err) {
      setError(err.message)
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[28px] font-medium tracking-[-0.035em] text-ink-primary">
          {t("dashboard.onboarding.review.title")}
        </h1>
        <p className="mt-2 text-sm text-ink-secondary leading-relaxed">
          {t("dashboard.onboarding.review.lede", { count: pages.length })}
        </p>
      </div>

      {loading && <p className="text-sm text-ink-tertiary">…</p>}

      {!loading && pages.length > 0 && (
        <div className="border-t border-rule-strong">
          <div className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,2.5fr)_32px] gap-4 font-mono text-[10px] tracking-[0.12em] text-ink-tertiary py-2 border-b border-rule">
            <span>{t("dashboard.onboarding.review.column_type")}</span>
            <span>{t("dashboard.onboarding.review.column_path")}</span>
            <span>{t("dashboard.onboarding.review.column_title")}</span>
            <span />
          </div>
          <ul>
            {pages.map((page) => (
              <li
                key={page.id}
                className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,2.5fr)_32px] gap-4 py-3 border-b border-rule text-sm items-center"
              >
                <span className="font-mono text-[11px] text-ink-tertiary uppercase tracking-wider">
                  {page.page_type}
                </span>
                <span className="font-mono text-[12px] text-ink-secondary truncate">{page.path}</span>
                <span className="text-ink-primary truncate">{page.title || "—"}</span>
                <button
                  type="button"
                  onClick={() => removePage(page)}
                  disabled={removingId === page.id}
                  aria-label={t("dashboard.onboarding.review.remove")}
                  className="text-ink-faint hover:text-failure transition-colors disabled:opacity-40"
                >
                  <Trash2 size={14} strokeWidth={1.75} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {blockedNotAdded.length > 0 && (
        <section className="space-y-3">
          <div>
            <p
              className="font-mono text-[10px] tracking-[0.15em] text-ink-tertiary"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {t("dashboard.onboarding.review.blocked_eyebrow")}
            </p>
            <p className="mt-1 text-sm text-ink-secondary leading-relaxed">
              {t("dashboard.onboarding.review.blocked_heading")}
            </p>
          </div>
          <ul className="border-t border-rule">
            {blockedNotAdded.map((path) => (
              <li
                key={path}
                className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 py-3 border-b border-rule items-center"
              >
                <span className="font-mono text-[12px] text-ink-secondary truncate">{path}</span>
                <button
                  type="button"
                  onClick={() => includeBlocked(path)}
                  disabled={includingPath === path}
                  className="text-ai text-[13px] font-medium disabled:opacity-60"
                >
                  {includingPath === path
                    ? t("dashboard.onboarding.review.including")
                    : t("dashboard.onboarding.review.include")}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <form
        onSubmit={addPage}
        className="border-t border-rule pt-6 grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] gap-3 items-end"
      >
        <label className="block">
          <span className="block font-mono text-[10px] tracking-[0.12em] text-ink-tertiary">
            {t("dashboard.onboarding.review.add_path_label")}
          </span>
          <input
            type="text"
            required
            value={newPath}
            onChange={(e) => setNewPath(e.target.value)}
            placeholder={t("dashboard.onboarding.review.add_path_placeholder")}
            className="mt-1 w-full bg-surface-card border border-rule rounded-button px-3 py-2 text-sm font-mono text-[12px] text-ink-primary placeholder:text-ink-faint focus:outline-none focus:border-brand"
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] tracking-[0.12em] text-ink-tertiary">
            {t("dashboard.onboarding.review.add_title_label")}
          </span>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="mt-1 w-full bg-surface-card border border-rule rounded-button px-3 py-2 text-sm text-ink-primary focus:outline-none focus:border-brand"
          />
        </label>
        <button
          type="submit"
          disabled={adding}
          className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-60 h-[38px]"
        >
          {adding
            ? t("dashboard.onboarding.review.add_submitting")
            : t("dashboard.onboarding.review.add_submit")}
        </button>
      </form>

      {addError && <p className="text-sm text-failure">{addError}</p>}
      {error && <p className="text-sm text-failure">{error}</p>}

      <div className="pt-2">
        <button
          type="button"
          onClick={markReviewed}
          disabled={submitting || loading}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {submitting
            ? t("dashboard.onboarding.review.submitting")
            : t("dashboard.onboarding.review.submit")}
        </button>
      </div>
    </div>
  )
}
